import { PremiumInlineToast, WordLimitsInlineToast } from '@/components/toast';
import { VoicePlayer } from '@/components/VoicePlayer';
import { listenTrialMaxWords } from '@/environment';
import { PlayerMode, playerModeAtom } from '@/hooks/useAudioPlayer';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import {
    CacheStatus,
    PolotnoStoreActivePageKeys,
    setPageProperty,
    useCurrentProjectId,
    useIsTtsOverLimit,
    usePolotnoActivePageState,
    useSetPolotnoActivePageState,
} from '@/pages/AdvancedEditor/hooks';
import useGetProject from '@/pages/AdvancedEditor/hooks/useGetProject';
import { validateTtsText } from '@/pages/AdvancedEditor/utils';
import { convertTextForModel } from '@/pages/AdvancedEditor/utils/breakConversion';
import { useAuth } from '@/services/auth';
import { AudioError, ttsGenerationErrors } from '@/state/presenters';
import { PlanGroup, Providers, VoiceAccess } from '@d-id/types';
import styled from '@emotion/styled';
import { TextField, TextFieldProps } from '@mui/material';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { updateSceneAudio } from '../ScriptPanel';
import { AddBreak } from './AddBreak';
export const MAIN_PLAYER_ID = 'main-player';
export const SILENT_PLAYER_ID = 'silent-player';

const TextSectionContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: calc(100% - 48px);
`;

const StyledTextField = styled(TextField)`
    height: 100%;
    padding: 14px 18px;
    margin-top: 4px;

    & .MuiInputBase-root {
        text-align: start;
        width: 100%;
        height: 100%;

        color: ${({ theme }) => theme.colors.secondary[900]};
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;

        ::before,
        ::after {
            border-bottom: none !important;
        }
    }

    & .Mui-focused fieldset {
        border-width: 1px !important;
    }

    & textarea {
        line-height: 1.5;
        height: 100% !important;
        overflow-y: unset !important;

        caret-color: ${({ theme }) => theme.colors.primary[900]};

        ::selection {
            background: rgba(255, 136, 46, 0.15);
        }

        ::placeholder {
            color: ${({ theme }) => theme.colors.secondary[500]};
            opacity: 1;
        }
    }

    label {
        top: 16px;
        left: 16px;
    }
`;

const ControlContainer = styled.div`
    margin-bottom: 8px;
    margin-left: 8px;
`;

const ListenContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 68px;
    background-color: ${({ theme }) => theme.colors.black[100]};
    padding: 14px 16px;
`;

const TextLength = styled.span<{ textTooLong?: boolean }>`
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    color: ${({ textTooLong, theme }) => (textTooLong ? theme.colors.error[650] : theme.colors.black[700])};
`;

const MonitContainer = styled.div`
    margin: 8px;

    > div {
        border-radius: 4px;
    }
`;

const ScriptTextField = ({ ...props }: TextFieldProps) => {
    return <StyledTextField {...props} multiline variant="standard" />;
};

export const TextSection = observer(() => {
    const { user, isAnonymous } = useAuth();
    const { store } = useAdvancedEditorContext();

    const [ttsText] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.textInput);
    const setTtsText = setPageProperty(store.activePage, PolotnoStoreActivePageKeys.textInput);
    const setSceneDurationCalculated = useSetPolotnoActivePageState(PolotnoStoreActivePageKeys.sceneDurationCalculated);
    const setTtsCreated = useSetPolotnoActivePageState(PolotnoStoreActivePageKeys.ttsCreated);
    const [ttsError, setTtsError] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.ttsError);
    const [ttsSelectedVoice] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.ttsSelectedVoice);
    const [uploadedAudio] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.uploadedAudio);

    const [voiceConfig] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.ttsVoiceConfig);
    const [ttsAudioCacheStatus, setTtsAudioCacheStatus] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.ttsAudioCacheStatus);

    const [cursorPosition, setCursorPosition] = useState(0);
    const playerMode = useRecoilValue(playerModeAtom);
    const { maxLength, textTooLong } = validateTtsText(ttsText, ttsSelectedVoice);

    const prevModelIdRef = useRef<string | undefined>();

    useEffect(() => {
        if (textTooLong) {
            setTtsError(ttsGenerationErrors['text-limit']);
        }
    }, [ttsText]);

    useEffect(() => {
        if (ttsSelectedVoice?.provider !== Providers.Elevenlabs) {
            prevModelIdRef.current = undefined;
            return;
        }

        const currentModelId = ttsSelectedVoice?.config?.modelId;

        if (prevModelIdRef.current !== undefined && prevModelIdRef.current !== currentModelId) {
            const convertedText = convertTextForModel(ttsText, currentModelId);

            if (convertedText !== ttsText) {
                runInAction(() => {
                    setTtsText(convertedText);
                    if (ttsAudioCacheStatus !== CacheStatus.Empty) {
                        setTtsAudioCacheStatus(CacheStatus.Invalid);
                    }
                });
            }
        }

        prevModelIdRef.current = currentModelId;
    }, [ttsSelectedVoice?.config?.modelId]);

    const projectId = useCurrentProjectId();
    const { data: project } = useGetProject(projectId);

    const shouldRestrict = isAnonymous || user?.planGroup === PlanGroup.TRIAL;
    const wordLimitExceeded = useIsTtsOverLimit(ttsText);
    const shouldRestrictVoice =
        (shouldRestrict || user?.planGroup === PlanGroup.LITE) &&
        ttsSelectedVoice?.access === VoiceAccess.Premium &&
        ttsText.length > 0 &&
        playerMode !== PlayerMode.Playing;

    const handleTextChange = useCallback(
        async (e: ChangeEvent<HTMLInputElement>) => {
            runInAction(async () => {
                setTtsText(e.target.value);
                setSceneDurationCalculated(false);
                setTtsCreated(false);

                setTtsError(null);

                if (uploadedAudio) {
                    updateSceneAudio({ page: store.activePage, audioData: null });
                }
                if (ttsAudioCacheStatus !== CacheStatus.Empty) {
                    setTtsAudioCacheStatus(CacheStatus.Invalid);
                }
            });
        },
        [
            setTtsText,
            setSceneDurationCalculated,
            setTtsError,
            uploadedAudio,
            project?.audio_url,
            ttsAudioCacheStatus,
            setTtsAudioCacheStatus,
        ]
    );

    return (
        <TextSectionContainer>
            <ScriptTextField
                id="text-container"
                data-testid="editor-script-text-container"
                placeholder="Type or record your script"
                value={ttsText}
                onChange={handleTextChange}
                onBlur={e => setCursorPosition(e.target.selectionStart ?? ttsText.length)}
                data-hj-allow
            />

            <MonitContainer>
                <WordLimitsInlineToast maxWords={listenTrialMaxWords} show={shouldRestrict && wordLimitExceeded} />
                <PremiumInlineToast show={!wordLimitExceeded && shouldRestrictVoice} />
            </MonitContainer>

            <ControlContainer>
                <AddBreak
                    text={ttsText}
                    setText={setTtsText}
                    cursorPosition={cursorPosition}
                    disabled={false}
                    ttsSelectedVoice={ttsSelectedVoice}
                />
            </ControlContainer>

            <ListenContainer>
                {ttsSelectedVoice && (
                    <VoicePlayer
                        voice={ttsSelectedVoice}
                        text={ttsText}
                        voiceConfig={voiceConfig}
                        ttsError={(ttsError?.key as AudioError) || null}
                        setTtsError={audioError => {
                            if (!audioError) {
                                return;
                            }

                            setTtsError(ttsGenerationErrors[audioError]);
                        }}
                    />
                )}

                <TextLength textTooLong={textTooLong}>
                    {ttsText.length.toLocaleString()}/{maxLength.toLocaleString()} characters
                </TextLength>
            </ListenContainer>
        </TextSectionContainer>
    );
});
